import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@material-ui/core';
import { Query } from '@material-table/core';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../../Api';
import { IGridQueryRequest, IGridQueryResult, OrderDirection } from '../../../../../Api/AptorApi';
import { MTable, RepealableName } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { LawListFilter } from '../../../../Administration/Features/LawPortal/CustomerLaws/CustomerLaws.Add.Filter';
import { ICustomerLaw } from '../../../../Administration/Features/LawPortal/CustomerLaws/CustomerLaws.List';
import { useOnboardingDraft } from './OnboardingDraft';

const Filters = memo(LawListFilter);
// Read the current draft even when the table retains a row action between renders.
const SelectionIcon = ({ id }: { id: number }) => {
  const { draft } = useOnboardingDraft();
  return <Checkbox checked={draft.selectedLaws.some(law => law.id === id)} tabIndex={-1} disableRipple style={{ padding: 0, pointerEvents: 'none' }} />;
};
export const OnboardingAddLegislation = () => {
  const { api } = useAptorApi();
  const { draft, update } = useOnboardingDraft();
  const { formatMessage: t } = useIntl();
  const table = useRef<any>(null);
  const [filters, setFilters] = useState<NonNullable<IGridQueryRequest['filters']>>({ status: ['1'] });
  const filterRef = useRef(filters);
  const inFlight = useRef(new Map<string, Promise<IGridQueryResult<ICustomerLaw>>>());
  const setFilter = useCallback<React.ComponentProps<typeof LawListFilter>['setFilter']>((field, value) => {
    setFilters(current => JSON.stringify(current[field]) === JSON.stringify(value) ? current : { ...current, [field]: value });
  }, []);

  useEffect(() => {
    if (filterRef.current === filters) return;
    filterRef.current = filters;
    table.current?.onQueryChange({ page: 0 });
  }, [filters]);

  const load = useCallback(async (query: Query<ICustomerLaw>) => {
    const request: IGridQueryRequest = {
      page: query.page, pageSize: query.pageSize, search: query.search || '',
      orderBy: String(query.orderBy?.field || 'number'),
      orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
      filters: filterRef.current, includeAllLaws: true,
    };
    const key = JSON.stringify(request);
    let pending = inFlight.current.get(key);
    if (!pending) {
      pending = api.searchNoneCustomerLaws<ICustomerLaw>(request).finally(() => inFlight.current.delete(key));
      inFlight.current.set(key, pending);
    }
    return pending;
  }, [api]);

  return <>
    <Filters setFilter={setFilter} />
    <MTable<ICustomerLaw> tableRef={table} actionLabel={t({ id: 'form.add' })} options={{ actionsColumnIndex: 0, pageSizeOptions: [20, 50, 100, 2500] }} data={load} columns={[
      { title: t({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
      { title: t({ id: 'law-portal.manage.law-list.table.law' }), field: 'name', render: law => <RepealableName name={law.name} effectiveTo={law.effectiveTo} /> },
      { title: t({ id: 'law-portal.manage.law-list.table.description' }), field: 'description', sorting: false, render: law => <HtmlView value={law.description} /> },
      { title: t({ id: 'law-portal.manage.law-list.table.area' }), field: 'area' },
    ]} actions={[law => ({ icon: () => <SelectionIcon id={law.id} />, tooltip: t({ id: 'form.add' }), onClick: () => update(current => {
      const selected = current.selectedLaws.some(item => item.id === law.id);
      const notes = { ...current.notes };
      const noteUnitIds = { ...current.noteUnitIds };
      if (selected) {
        delete noteUnitIds[law.id];
        Object.keys(notes).filter(key => key === String(law.id) || key.startsWith(`${law.id}:`)).forEach(key => { delete notes[key]; });
      }
      return {
        ...current,
        selectedLaws: selected ? current.selectedLaws.filter(item => item.id !== law.id) : [...current.selectedLaws, { id: law.id, name: law.name, number: law.number }],
        unitIds: current.units.map(unit => unit.id),
        noteLawIds: selected ? (current.noteLawIds || []).filter(id => id !== law.id) : current.noteLawIds,
        noteUnitIds,
        notes,
      };
    }) })]} />
  </>;
};
